// The language toggle (shell-ia §9.2; DESIGN-SYSTEM §00.2): a 28 px toolbar button naming the other language ("ع" in English, "EN" in
// Arabic; its accessible name says it in full), flips `lang` and `dir` on
// <html> and stores `pref:ui.locale` so the choice follows the person; `ncp.locale` keeps the first paint right. WP-SHELL.
import { useCallback, useEffect, useRef } from "react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Tooltip } from "@ncp/ui";
import { usePrefs, useSetPref } from "../prefs";

export function useLanguageSwitch(): () => void {
  const { locale, setLocale } = useLocale();
  const setPref = useSetPref();
  return useCallback(() => {
    const next = locale === "ar" ? "en" : "ar";
    setLocale(next);
    setPref("ui.locale", next);
  }, [locale, setLocale, setPref]);
}

/** Applies the stored language once per sign-in, when the server copy of the preferences arrives (another device may have changed it). */
export function useStoredLanguage(): void {
  const { values, synced } = usePrefs();
  const { locale, setLocale } = useLocale();
  const applied = useRef(false);
  useEffect(() => {
    if (!synced || applied.current) return;
    applied.current = true;
    const stored = values["ui.locale"];
    if ((stored === "en" || stored === "ar") && stored !== locale) setLocale(stored);
  }, [synced, values, locale, setLocale]);
}

export function LanguageToggle({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const toggle = useLanguageSwitch();
  const other = locale === "ar" ? "en" : "ar";
  return (
    <Tooltip content={t("assistant:econic.shell.lang.switch")} side="bottom">
      <button type="button" className={`es-icon-btn lang ${className}`} lang={other} onClick={toggle} aria-label={t("assistant:econic.shell.lang.switch")} data-econic-lang="">
        {other === "ar" ? "ع" : "EN"}
      </button>
    </Tooltip>
  );
}
